import { Fragment, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { BellIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { HiMenuAlt2 } from "react-icons/hi";
import { BsFillCreditCardFill, BsFillInfoSquareFill } from "react-icons/bs";
import { TbSquareArrowUpFilled } from "react-icons/tb";
import { FaUserAlt } from "react-icons/fa";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import HervestLogo from "../../assets/images/Union.svg";
import SplashScreen from "../SplashScreen/SplashScreen";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import Logout from "../../pages/Logout/Logout";
import AutomaticLogout from "../../AutomaticLogout";
import { FaBagShopping, FaUserGroup } from "react-icons/fa6";
import { MdWindow } from "react-icons/md";
import { IoCash } from "react-icons/io5";
import { AiFillPieChart } from "react-icons/ai";
import { IoLogOut } from "react-icons/io5";
import { BiSolidReceipt } from "react-icons/bi";
import AppDownloadBanner from "../AppDownloadBanner/AppDownloadBanner";
import HervestNotification from "../Modal/HervestNotification";

const navigation = [
  {
    name: "Dashboard",
    href: "/dashboard",
    icon: MdWindow,
    current: false,
  },
  { name: "Savings", href: "/plans", icon: BiSolidReceipt, current: true },
  { name: "Purse", href: "/purse", icon: FaBagShopping, current: false },
  {
    name: "Investments",
    href: "/investments",
    icon: TbSquareArrowUpFilled,
    current: false,
  },
  {
    name: "Loan",
    href: "/loan",
    icon: IoCash,
    current: false,
  },
  {
    name: "Banks & Cards",
    href: "/bank",
    icon: BsFillCreditCardFill,
    current: false,
  },
  {
    name: "Referral Code",
    href: "/referral",
    icon: FaUserGroup,
    current: false,
  },
  { name: "Account", href: "/profile", icon: FaUserAlt, current: false },
  {
    name: "Portfolio",
    href: "/portfolio",
    icon: AiFillPieChart,
    current: false,
  },
  {
    name: "About HerVest",
    href: "/about-hervest",
    icon: BsFillInfoSquareFill,
    current: false,
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const variants = {
  hidden: {
    opacity: 0,
    x: "-1vw", // Start off-screen to the left
  },
  visible: {
    opacity: 1,
    x: 0, // Slide in to the center
    transition: {
      duration: 1,
    },
  },
};

export default function DashboardLayout({ children, title }) {
  const { user } = useSelector((state) => state.userDetails);
  const { stats } = useSelector((state) => state.myStat);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [logoutOpen, SetLogoutOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);

  const { token } = useSelector((state) => state.auth);

  useEffect(() => {
    setLoading(true);

    if (!token) {
      return (window.location.href = "/");
    }
    setLoading(false);

    setTimeout(() => {
      setLoading(false);
    }, 4000); // Adjust the time as needed
  }, [token]);

  useEffect(() => {
    // user/stats load asynchronously; Beacon throws "Invalid Email address"
    // when identify() is given the string "undefined", so wait for real data
    // and only send the fields that exist.
    if (!user?.email || typeof window.Beacon !== "function") return;

    const fields = {
      name: [user.first_name, user.last_name].filter(Boolean).join(" "),
      email: user.email,
      avatar: user.profile_image,
      signature: localStorage.getItem("hashSignature"),
      "mobile-number": user.phone,
      "date-of-birth": user.birth,
      "purse-balance": stats?.totalPurseBalance,
      "total-investments": stats?.totalInvestment,
      "total-savings": stats?.totalSavings,
      "referral-code": user.referrer_code,
      "user-id": user.id,
    };
    const payload = Object.fromEntries(
      Object.entries(fields).filter(([, v]) => v !== undefined && v !== null && v !== "")
    );

    try {
      window.Beacon("identify", payload);
    } catch (e) {
      console.warn("Beacon identify failed", e);
    }
  }, [user, stats]);

  return (
    <>
      <HervestNotification
        show={notificationOpen}
        close={() => {
          setNotificationOpen(false);
        }}
        title="title"
        subTitle="subTitle"
        onClick={() => {}}
        btnText="btnText"
      />
      <Logout show={logoutOpen} close={SetLogoutOpen} />
      <AppDownloadBanner />
      <AutomaticLogout>
        {loading && <SplashScreen />}

        {!loading && (
          <div>
            <Transition.Root show={sidebarOpen} as={Fragment}>
              <Dialog
                as="div"
                className="relative z-50 lg:hidden"
                onClose={setSidebarOpen}
              >
                <Transition.Child
                  as={Fragment}
                  enter="transition-opacity ease-linear duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="transition-opacity ease-linear duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="fixed inset-0 bg-gray-900/80" />
                </Transition.Child>

                <div className="fixed inset-0 flex">
                  <Transition.Child
                    as={Fragment}
                    enter="transition ease-in-out duration-300 transform"
                    enterFrom="-translate-x-full"
                    enterTo="translate-x-0"
                    leave="transition ease-in-out duration-300 transform"
                    leaveFrom="translate-x-0"
                    leaveTo="-translate-x-full"
                  >
                    <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                      <Transition.Child
                        as={Fragment}
                        enter="ease-in-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in-out duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                          <button
                            type="button"
                            className="-m-2.5 p-2.5"
                            onClick={() => setSidebarOpen(false)}
                          >
                            <span className="sr-only">Close sidebar</span>
                            <XMarkIcon
                              className="h-6 w-6 text-white"
                              aria-hidden="true"
                            />
                          </button>
                        </div>
                      </Transition.Child>

                      {/* MOBILE RESPONSIVENESS SIDEBAR */}
                      <div className="flex grow flex-col gap-y-5 p-16 overflow-y-auto bg-white ">
                        <div className="flex shrink-0  justify-start items-center mb-3">
                          <img
                            loading="lazy"
                            className="h-8 w-auto object-cover"
                            src={HervestLogo}
                            alt="Hervest"
                          />
                        </div>
                        <nav className="flex flex-1 flex-col">
                          <ul className="flex flex-1 flex-col gap-y-7">
                            <li>
                              <ul className="-mx-2 space-y-1">
                                {navigation.map((item) => (
                                  <li key={item.name}>
                                    <Link
                                      to={item.href}
                                      className={classNames(
                                        location.pathname === item.href
                                          ? "bg-secondary text-gray font-extrabold"
                                          : "text-gray hover:bg-secondary",
                                        `group flex gap-x-3 rounded-xl p-3 text-sm leading-6 ${
                                          item.name === "Purse" && "purse-demo"
                                        }`
                                      )}
                                    >
                                      <item.icon
                                        className={classNames(
                                          location.pathname === item.href
                                            ? "text-primary font-extrabold"
                                            : "text-[#666666] group-hover:text-primary",
                                          "h-5 w-5 shrink-0"
                                        )}
                                        aria-hidden="true"
                                      />
                                      {item.name}
                                    </Link>
                                  </li>
                                ))}

                                <li onClick={() => SetLogoutOpen(true)}>
                                  <p className="group flex cursor-pointer gap-x-3 rounded-xl p-2.5 text-sm leading-6 ">
                                    <IoLogOut
                                      className="h-6 w-6 shrink-0 text-[#666666] group-hover:text-primary"
                                      aria-hidden="true"
                                    />
                                    Logout
                                  </p>
                                </li>
                              </ul>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </Dialog>
            </Transition.Root>

            {/* Static sidebar for Large Screens*/}
            <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
              <div className="flex grow flex-col gap-y-5 p-8 overflow-y-auto bg-white text-gray px-10 pb-4 border-r-4 border-primary">
                <div className="flex shrink-0 items-center">
                  <a href="/dashboard">
                    <img
                      loading="lazy"
                      className="h-8 w-auto object-cover"
                      src={HervestLogo}
                      alt="Hervest"
                    />
                  </a>
                </div>
                <nav className="flex flex-1 flex-col">
                  <ul className="flex flex-1 flex-col gap-y-7">
                    <li>
                      <ul className="-mx-2 space-y-1">
                        {navigation.map((item) => (
                          <li key={item.name}>
                            <Link
                              to={item.href}
                              className={classNames(
                                location.pathname === item.href
                                  ? "bg-secondary text-gray font-extrabold"
                                  : "text-gray hover:bg-secondary",
                                `group flex gap-x-3 rounded-xl p-2.5 text-sm leading-6  ${
                                  item.name === "Purse" && "purse-demo"
                                }`
                              )}
                            >
                              <item.icon
                                className={classNames(
                                  location.pathname === item.href
                                    ? "text-primary font-extrabold"
                                    : "text-[#666666] group-hover:text-primary",
                                  "h-5 w-5 shrink-0"
                                )}
                                aria-hidden="true"
                              />
                              {item.name}
                            </Link>
                          </li>
                        ))}

                        <li onClick={() => SetLogoutOpen(true)}>
                          <p className="group flex cursor-pointer gap-x-3 rounded-xl p-2.5 text-sm leading-6 ">
                            <IoLogOut
                              className="h-6 w-6 shrink-0 text-[#666666] group-hover:text-primary"
                              aria-hidden="true"
                            />
                            Logout
                          </p>
                        </li>
                      </ul>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>

            <div className="lg:pl-72">
              <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-white bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
                <button
                  type="button"
                  className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
                  onClick={() => setSidebarOpen(true)}
                >
                  <span className="sr-only">Open sidebar</span>
                  <HiMenuAlt2 className="h-6 w-6" aria-hidden="true" />
                </button>

                <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
                  <span className="relative flex flex-1">
                    <div className="flex justify-start items-center text-[#33343D] text-[15px] font-[500px] leading-7 BoldFonter pt-6 md:pb-6">
                      {title}
                    </div>
                  </span>
                  <div className="flex items-center gap-x-4 lg:gap-x-6">
                    <button
                      type="button"
                      onClick={() => setNotificationOpen(true)}
                      className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500"
                    >
                      <BellIcon
                        className="h-6 w-6 text-black notification-demo"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </div>
              </div>

              <main className="py-5 overflow-x-hidden">
                <div className="px-4 sm:px-6 lg:px-8">
                  <motion.div
                    variants={variants}
                    initial="hidden"
                    animate="visible"
                  >
                    {children}
                  </motion.div>
                </div>
              </main>
            </div>
          </div>
        )}
      </AutomaticLogout>
    </>
  );
}
